import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { FiberCircuitListItemType, FiberCircuitType } from '../../types/fiber/fiber.types';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import {
  deleteFiberCircuitDetails,
  getFiberCircuitDetails,
  getFiberCircuitsList,
  patchFiberCircuitDetails,
  postFiberCircuitDetails,
} from './fiber.action';
import snackbarUtils from '../../utils/SnackbarUtils';

export interface FiberState {
  fiberCircuitsRouteParams: RouteParamsType;
  fiberCircuitsList: null | PaginationType<FiberCircuitListItemType>;
  fiberCircuitsListFetchStatus: FETCH_STATUS;
  fiberCircuitDetails: null | FiberCircuitType;
  fiberCircuitDetailsFetchStatus: FETCH_STATUS;
  fiberCircuitDetailsPostStatus: FETCH_STATUS;
  fiberCircuitDetailsPatchStatus: FETCH_STATUS;
  fiberCircuitDetailsDeleteStatus: FETCH_STATUS;
}

export const initialFiberCircuitsRouteParams = {
  page: 1,
  limit: 10,
  order: '-fiber_circuit.id',
  q: '',
};

const INIT_STATE: FiberState = {
  fiberCircuitsRouteParams: initialFiberCircuitsRouteParams,
  fiberCircuitsList: null,
  fiberCircuitsListFetchStatus: FETCH_STATUS.NULL,
  fiberCircuitDetails: null,
  fiberCircuitDetailsFetchStatus: FETCH_STATUS.NULL,
  fiberCircuitDetailsPostStatus: FETCH_STATUS.NULL,
  fiberCircuitDetailsPatchStatus: FETCH_STATUS.NULL,
  fiberCircuitDetailsDeleteStatus: FETCH_STATUS.NULL,
};

export const fiberSlice = createSlice({
  name: 'fiber',
  initialState: INIT_STATE,
  reducers: {
    setFiberCircuitsList: (state, action) => {
      state.fiberCircuitsList = action.payload;
    },
    setFiberCircuitsListFetchStatus: (state, action) => {
      state.fiberCircuitsListFetchStatus = action.payload;
    },
    setFiberCircuitDetails: (state, action) => {
      state.fiberCircuitDetails = action.payload;
    },
    setFiberCircuitDetailsFetchStatus: (state, action) => {
      state.fiberCircuitDetailsFetchStatus = action.payload;
    },
    setFiberCircuitDetailsPostStatus: (state, action) => {
      state.fiberCircuitDetailsPostStatus = action.payload;
    },
    setFiberCircuitDetailsPatchStatus: (state, action) => {
      state.fiberCircuitDetailsPatchStatus = action.payload;
    },
    setFiberCircuitDetailsDeleteStatus: (state, action) => {
      state.fiberCircuitDetailsDeleteStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'fiberCircuits') {
        state.fiberCircuitsRouteParams = { ...state.fiberCircuitsRouteParams, ...payload.data };
      }
    });

    // CREATE FIBEER CIRCUIT DETAILS
    builder.addCase(postFiberCircuitDetails.pending, state => {
      state.fiberCircuitDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postFiberCircuitDetails.rejected, (state, action) => {
      state.fiberCircuitDetailsPostStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    builder.addCase(postFiberCircuitDetails.fulfilled, state => {
      state.fiberCircuitDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Fiber Circuit created successfully');
      });
    });

    // GET FIBEER CIRCUIT LIST
    builder.addCase(getFiberCircuitsList.pending, state => {
      state.fiberCircuitsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getFiberCircuitsList.rejected, (state, action) => {
      if (action.payload) {
        state.fiberCircuitsListFetchStatus = FETCH_STATUS.REJECTED;
        // state.fiberCircuitsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getFiberCircuitsList.fulfilled, (state, action) => {
      state.fiberCircuitsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.fiberCircuitsList = action.payload;
    });

    // GET FIBEER CIRCUIT DETAILS
    builder.addCase(getFiberCircuitDetails.pending, state => {
      state.fiberCircuitDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getFiberCircuitDetails.rejected, (state, action) => {
      state.fiberCircuitDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.fiberCircuitDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getFiberCircuitDetails.fulfilled, (state, action) => {
      state.fiberCircuitDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.fiberCircuitDetails = action.payload;
    });

    // PATCH FIBEER CIRCUIT DETAILS
    builder.addCase(patchFiberCircuitDetails.pending, state => {
      state.fiberCircuitDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchFiberCircuitDetails.rejected, (state, action) => {
      state.fiberCircuitDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchFiberCircuitDetails.fulfilled, state => {
      state.fiberCircuitDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Fiber Circuit updated successfully');
      });
    });

    // DELETE FIBEER CIRCUIT DETAILS
    builder.addCase(deleteFiberCircuitDetails.pending, state => {
      state.fiberCircuitDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteFiberCircuitDetails.rejected, (state, action) => {
      state.fiberCircuitDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteFiberCircuitDetails.fulfilled, state => {
      state.fiberCircuitDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Fiber Circuit deleted successfully');
      });
    });
  },
});

export const {
  setFiberCircuitDetailsDeleteStatus,
  setFiberCircuitDetailsFetchStatus,
  setFiberCircuitDetailsPatchStatus,
  setFiberCircuitDetailsPostStatus,
  setFiberCircuitsListFetchStatus,
  setFiberCircuitDetails,
  setFiberCircuitsList,
} = fiberSlice.actions;

export default fiberSlice.reducer;
